import React from "react";
import KeyboardArrowLeft from "@components/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@components/icons/KeyboardArrowRight";
import FirstPageIcon from "@components/icons/FirstPage";
import LastPageIcon from "@components/icons/LastPage";
import cn from "./helpers/cn";
import IconButton from "./IconButton";

export type RowsPerPageOption = number | { value: number; label: string };

export interface LabelDisplayedRowsArgs {
  from: number;
  to: number;
  count: number;
  page: number;
}

interface TablePaginationProps extends Omit<React.HTMLAttributes<HTMLElement>, "onChange"> {
  component?: React.ElementType;
  count: number;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions?: ReadonlyArray<RowsPerPageOption>;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  onRowsPerPageChange?: React.ChangeEventHandler<HTMLSelectElement>;
  labelRowsPerPage?: React.ReactNode;
  labelDisplayedRows?: (args: LabelDisplayedRowsArgs) => React.ReactNode;
  showFirstButton?: boolean;
  showLastButton?: boolean;
  SelectProps?: React.SelectHTMLAttributes<HTMLSelectElement>;
  backIconButtonProps?: Partial<React.ComponentProps<typeof IconButton>>;
  nextIconButtonProps?: Partial<React.ComponentProps<typeof IconButton>>;
  colSpan?: number;
}

const defaultLabelDisplayedRows = ({ from, to, count }: LabelDisplayedRowsArgs) =>
  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`;

const TablePagination = React.forwardRef<HTMLElement, TablePaginationProps>((props, ref) => {
  const {
    className,
    component: Component = "td",
    count,
    page,
    rowsPerPage,
    rowsPerPageOptions = [10, 25, 50, 100],
    onPageChange,
    onRowsPerPageChange,
    labelRowsPerPage = "Rows per page:",
    labelDisplayedRows = defaultLabelDisplayedRows,
    showFirstButton = false,
    showLastButton = false,
    SelectProps,
    backIconButtonProps,
    nextIconButtonProps,
    colSpan: colSpanProp,
    ...other
  } = props;

  const lastPage = count > 0 && rowsPerPage > 0 ? Math.max(0, Math.ceil(count / rowsPerPage) - 1) : 0;
  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = count !== -1 ? Math.min(count, (page + 1) * rowsPerPage) : (page + 1) * rowsPerPage;

  const handleFirst = (e: React.MouseEvent<HTMLButtonElement>) => onPageChange(e, 0);
  const handleBack = (e: React.MouseEvent<HTMLButtonElement>) => onPageChange(e, page - 1);
  const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => onPageChange(e, page + 1);
  const handleLast = (e: React.MouseEvent<HTMLButtonElement>) => onPageChange(e, lastPage);

  const colSpan = Component === "td" || Component === "th" ? (colSpanProp ?? 1000) : undefined;

  return (
    <Component
      ref={ref}
      colSpan={colSpan}
      className={cn("text-slate-700 text-sm border-t border-slate-200", className)}
      {...other}
    >
      <div className="flex items-center justify-end flex-wrap min-h-[52px] px-2">
        <div className="flex-1" />

        {rowsPerPageOptions.length > 1 && (
          <p className="shrink-0 mr-2">{labelRowsPerPage}</p>
        )}

        {rowsPerPageOptions.length > 1 && (
          <select
            value={rowsPerPage}
            onChange={onRowsPerPageChange}
            className={cn(
              "shrink-0 mr-8 cursor-pointer bg-transparent border-0 outline-none pr-6 pl-2 py-1",
              "text-sm hover:bg-black/[0.04] rounded"
            )}
            {...SelectProps}
          >
            {rowsPerPageOptions.map((option) => {
              const value = typeof option === "number" ? option : option.value;
              const label = typeof option === "number" ? option : option.label;

              return (
                <option key={value} value={value}>
                  {label}
                </option>
              );
            })}
          </select>
        )}

        <p className="shrink-0 mr-2">
          {labelDisplayedRows({ from, to, count, page })}
        </p>

        <div className="flex items-center shrink-0 ml-5">
          {showFirstButton && (
            <IconButton
              onClick={handleFirst}
              disabled={page === 0}
              aria-label="first page"
              size="small"
            >
              <FirstPageIcon />
            </IconButton>
          )}

          <IconButton
            onClick={handleBack}
            disabled={page === 0}
            aria-label="previous page"
            size="small"
            {...backIconButtonProps}
          >
            <KeyboardArrowLeft />
          </IconButton>

          <IconButton
            onClick={handleNext}
            disabled={count !== -1 ? page >= lastPage : false}
            aria-label="next page"
            size="small"
            {...nextIconButtonProps}
          >
            <KeyboardArrowRight />
          </IconButton>

          {showLastButton && (
            <IconButton
              onClick={handleLast}
              disabled={page >= lastPage}
              aria-label="last page"
              size="small"
            >
              <LastPageIcon />
            </IconButton>
          )}
        </div>
      </div>
    </Component>
  );
});

TablePagination.displayName = "TablePagination";

export default TablePagination;
